// Tab Personal — jubilables, distribución por área, top tardanzas/ausencias.

import React, { useEffect, useState } from 'react';
import {
    Box, Paper, Typography, Grid, Skeleton, Alert,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    List, ListItem, ListItemText,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { dashEjecPersonalJWT } from '../../api/endpoints';

const PRIMARY = '#1769aa';
const BR = 4;
const fmtNum = (n) => Number(n || 0).toLocaleString('es-AR');

export default function TabPersonal() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancel = false;
        dashEjecPersonalJWT().then(r => { if (!cancel && r?.ok) setData(r.data); }).finally(() => { if (!cancel) setLoading(false); });
        return () => { cancel = true; };
    }, []);

    if (loading) {
        return (
            <Box>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    {[1, 2, 3].map(i => <Grid key={i} size={{ xs: 12, md: 4 }}><Skeleton variant="rounded" height={86} sx={{ borderRadius: `${BR}px` }} /></Grid>)}
                </Grid>
                <Skeleton variant="rounded" height={300} sx={{ borderRadius: `${BR}px` }} />
            </Box>
        );
    }
    if (!data) return <Alert severity="error">No se pudieron cargar los datos.</Alert>;

    const tot = data.totales || {};

    return (
        <Box>
            {/* Totales */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                {[
                    { label: 'Internos activos', value: tot.internos, color: '#1565c0' },
                    { label: 'Externos activos', value: tot.externos, color: '#7b1fa2' },
                    { label: 'Personal en baja', value: tot.bajas, color: '#616161' },
                ].map((b, i) => (
                    <Grid key={i} size={{ xs: 12, sm: 4 }}>
                        <Paper sx={{ p: 1.5, borderRadius: `${BR}px` }}>
                            <Typography variant="caption" sx={{ color: '#666', textTransform: 'uppercase', fontSize: '0.72rem' }}>
                                {b.label}
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: b.color, mt: 0.5 }}>
                                {fmtNum(b.value)}
                            </Typography>
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            <Grid container spacing={2}>
                {/* Jubilables */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 2, borderRadius: `${BR}px`, height: '100%', border: '1px solid #ed6c02', background: '#fff8e1' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#e65100' }}>
                            ⏰ Personal próximo a jubilar (90 días) — {(data.jubilables || []).length}
                        </Typography>
                        {(data.jubilables || []).length === 0 ? (
                            <Typography color="text.secondary">Nadie próximo a jubilar en 90 días</Typography>
                        ) : (
                            <List dense>
                                {(data.jubilables || []).map((p, i) => (
                                    <ListItem key={i} disablePadding sx={{ py: 0.25 }}>
                                        <ListItemText
                                            primary={<Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{p.nombre}</Typography>}
                                            secondary={
                                                <Typography variant="caption" sx={{ color: '#666', fontSize: '0.72rem' }}>
                                                    {p.Funcion || '—'} · {p.Area || '—'} · cumple en {p.dias_para_jub} día(s)
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </Paper>
                </Grid>

                {/* Distribución por área */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 2, borderRadius: `${BR}px`, height: '100%' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: PRIMARY }}>
                            Personal activo por área
                        </Typography>
                        {(data.porArea || []).length > 0 && (
                            <BarChart
                                height={Math.max(280, (data.porArea || []).length * 22)}
                                layout="horizontal"
                                yAxis={[{ scaleType: 'band', data: (data.porArea || []).map(a => (a.area || '').slice(0, 30)) }]}
                                xAxis={[{}]}
                                series={[{ data: (data.porArea || []).map(a => Number(a.cant || 0)), color: PRIMARY }]}
                                margin={{ top: 10, right: 20, bottom: 30, left: 220 }}
                            />
                        )}
                    </Paper>
                </Grid>

                {/* Top tardanzas + Top ausencias */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 2, borderRadius: `${BR}px`, height: '100%' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#e65100' }}>
                            Top 10 tardanzas — mes actual
                        </Typography>
                        <TableContainer sx={{ maxHeight: 360 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>Personal</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>Cant.</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>Min.</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(data.topTardanzas || []).length === 0 ? (
                                        <TableRow><TableCell colSpan={3} sx={{ textAlign: 'center', color: '#999', py: 2 }}>Sin tardanzas este mes</TableCell></TableRow>
                                    ) : (data.topTardanzas || []).map((t, i) => (
                                        <TableRow key={i} hover>
                                            <TableCell sx={{ fontSize: '0.82rem' }}>{t.nombre}</TableCell>
                                            <TableCell align="right" sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{t.cant_tardanzas}</TableCell>
                                            <TableCell align="right" sx={{ fontSize: '0.78rem', color: '#e65100' }}>{t.total_min}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 2, borderRadius: `${BR}px`, height: '100%' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#c62828' }}>
                            Top 10 ausencias — mes actual
                        </Typography>
                        <TableContainer sx={{ maxHeight: 360 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>Personal</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>Cant.</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(data.topAusencias || []).length === 0 ? (
                                        <TableRow><TableCell colSpan={2} sx={{ textAlign: 'center', color: '#999', py: 2 }}>Sin ausencias este mes</TableCell></TableRow>
                                    ) : (data.topAusencias || []).map((a, i) => (
                                        <TableRow key={i} hover>
                                            <TableCell sx={{ fontSize: '0.82rem' }}>{a.nombre}</TableCell>
                                            <TableCell align="right" sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#c62828' }}>{a.cant_ausencias}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}
